import {useEffect, useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {useLocation} from 'react-router-dom';
import {useQueryClient} from '@tanstack/react-query';
import ReportJobsList from './list';
import {readJobId} from 'src/helpers/jobIdParam';
import {useNotificationClick} from 'src/helpers/notificationClick';
import {useReportJobs, useReportJobLookup, reportJobsKeys, ReportJobPage} from './queries';
import useAlerts from 'src/hooks/use-alerts';
import useSocket from 'src/hooks/use-socket';
import useStore from 'src/hooks/use-store';

/**
 * "Relatórios Solicitados": pedidos de relatório em fila, em processamento ou já
 * prontos. Só relatórios exportados pela fila aparecem aqui -- relatórios em tela não geram
 * pedido, e o usuário aguarda o tempo necessário nesse caso.
 *
 * Reconsulta a lista inteira em qualquer evento `reportJobs` do socket, em vez de aplicar o
 * job recebido direto no cache: mais simples, e o volume de eventos por usuário é baixo o
 * suficiente (um por pedido concluído ou falhado) para não pesar.
 *
 * O link da notificação de relatório pronto abre esta tela com `?jobId=<id>` (ou, pelo sino em /fleet e
 * /client, com `jobId` no estado da navegação). Uma consulta à parte
 * (`useReportJobLookup`) pergunta ao servidor em que página o pedido está; a tela adota essa página
 * e passa o `jobId` à lista, que realça a linha. A lista em si nunca leva `jobId`, então reconsulta por
 * socket ou por intervalo fica na página que o usuário está olhando. Cada clique numa notificação
 * (`helpers/notificationClick`) dispara uma consulta: uma 2ª notificação com a tela aberta, de outro
 * pedido ou do mesmo, leva à página do pedido e refaz o realce, mesmo que o usuário tenha mudado de
 * página nesse meio tempo. Outra mudança de localização com o mesmo jobId na URL não consulta de novo.
 * Se a consulta do link falha, a tela avisa uma vez por clique e deixa o usuário onde está.
 *
 * Página atual vazia com pedidos em outras (os da última página expiraram ou foram removidos): volta
 * para a última página que existe.
 *
 * O aviso de relatório grande demais para o e-mail não é daqui: ele aparece em qualquer tela e vive em
 * `src/providers/report-job-notice`, montado na raiz do app.
 */
const EMPTY_PAGE: ReportJobPage = {items: [], total: 0, page: 0};

const ReportJobsContainer = () => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [settled, setSettled] = useState<{jobId: number, click: string | null, failed: boolean} | null>(null);
  const location = useLocation();
  const click = useNotificationClick();
  // `?jobId` na URL vem do link da tela /admin/manage/reportjobs; o sino, em /fleet e /client, manda o jobId
  // no estado da navegação, porque lá a query é do relatório aberto no mapa.
  const stateJobId = (location.state as {jobId?: number} | undefined)?.jobId;
  const linkedJobId = useMemo(
      () => readJobId(location.search) ?? (Number.isSafeInteger(stateJobId) ? stateJobId as number : null),
      [location.search, stateJobId],
  );
  // `click` é o último valor de clique visto: um replace sem estado (fechar um modal, trocar o hash) não
  // o muda, então não abre uma consulta nova nem desliga a que está em andamento.
  const linkSettled = settled !== null && settled.jobId === linkedJobId && settled.click === click;
  const lookup = useReportJobLookup(linkedJobId, pageSize, click, !linkSettled);
  const intl = useIntl();
  const {toast} = useAlerts();
  const {data = EMPTY_PAGE, isFetching: fetching} = useReportJobs(page, pageSize);
  const queryClient = useQueryClient();
  const {reportJobs: reportJobs$} = useSocket();
  const {store} = useStore();
  const currentUserId = store.auth.user.id;

  useEffect(() => {
    const subscription = reportJobs$.subscribe(() => {
      queryClient.invalidateQueries(reportJobsKeys.all);
    });
    return () => subscription.unsubscribe();
  }, [reportJobs$, queryClient]);

  // BUSINESS RULE: a página que o servidor devolve para o pedido do link vale mais que a página
  // escolhida à mão enquanto a resposta não chegava. Cada clique é desfeito uma vez, com êxito ou
  // falha: o desfecho fica registrado e a consulta é desligada, então reconsulta por socket ou
  // reconexão não a repete nem puxa a página de quem já paginou. Clicar de novo na mesma notificação
  // procura o pedido outra vez. Sem jobId na URL (voltar no navegador, por exemplo) o desfecho é
  // esquecido, porque voltar e avançar restauram o estado de antes.
  useEffect(() => {
    if (linkedJobId === null) {
      setSettled(null);
      return;
    }
    if (linkSettled) {
      return;
    }
    if (lookup.isError) {
      toast.error(intl.formatMessage({id: 'reportLoadError'}));
      setSettled({jobId: linkedJobId, click, failed: true});
      return;
    }
    if (!lookup.data) {
      return;
    }
    // A página que o servidor devolveu já traz os pedidos; guardá-la na chave da lista evita abrir
    // a página nova vazia até a consulta dela voltar.
    queryClient.setQueryData(reportJobsKeys.list(lookup.data.page, pageSize), lookup.data);
    setPage(lookup.data.page);
    setSettled({jobId: linkedJobId, click, failed: false});
  }, [linkedJobId, click, linkSettled, lookup.data, lookup.isError, pageSize, queryClient, toast, intl]);

  // CONSTRAINT: só age com a consulta do link resolvida: sem link, adotada ou falha (falha nunca adota,
  // então não há conflito). Esta função enxerga a página de antes da adoção; se rodasse na mesma
  // passada que ela, o recuo sobrescreveria a página adotada.
  const linkResolved = linkedJobId === null || linkSettled || lookup.isError;
  useEffect(() => {
    if (linkResolved && !fetching && data.items.length === 0 && data.total > 0 && page > 0) {
      const lastPage = Math.ceil(data.total / pageSize) - 1;
      if (lastPage < page) {
        setPage(lastPage);
      }
    }
  }, [linkResolved, fetching, data, page, pageSize]);

  return (
    <ReportJobsList
      reportJobs={data.items}
      total={data.total}
      page={page}
      pageSize={pageSize}
      onPageChange={setPage}
      onPageSizeChange={(size) => {
        setPageSize(size);
        setPage(0);
      }}
      loading={fetching}
      currentUserId={currentUserId}
      linkedJobId={linkSettled && !settled.failed ? linkedJobId : null}
    />
  );
};

export default ReportJobsContainer;
